import { useState, useEffect } from 'react';

/**
 * Hook to detect and monitor network status (online/offline)
 * @returns Object containing online status and connection quality metrics
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionQuality, setConnectionQuality] = useState<'good' | 'poor' | 'unknown'>('unknown');

  useEffect(() => {
    // Update online status
    const handleOnline = () => {
      setIsOnline(true);
      // Check connection quality after going online
      checkConnectionQuality();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setConnectionQuality('unknown');
    };

    // Custom event handler for simulating connection changes (used for testing)
    const handleConnectionChange = (event: Event) => {
      const customEvent = event as CustomEvent<{isOnline: boolean; connectionQuality: 'good' | 'poor' | 'unknown'}>;
      if (customEvent.detail) {
        setIsOnline(customEvent.detail.isOnline);
        setConnectionQuality(customEvent.detail.connectionQuality);
      }
    };

    // Check connection quality using Navigation API if available
    const checkConnectionQuality = () => {
      if ('connection' in navigator && 'connection' in window.navigator) {
        const connection = (navigator as any).connection;
        
        if (connection?.downlink !== undefined) {
          // Consider below 0.5 Mbps as poor connection - more lenient threshold
          // Prevent false positives for moderately slow but usable connections
          setConnectionQuality(connection.downlink >= 0.5 ? 'good' : 'poor');
        } else if (connection?.effectiveType) {
          // Fallback to effective connection type if downlink is not available
          setConnectionQuality(
            ['4g', '3g'].includes(connection.effectiveType) ? 'good' : 'poor'
          );
        } else {
          // Default to good if we can't determine quality but we're online
          setConnectionQuality('good');
        }
      }
    };

    // Initial connection quality check
    if (isOnline) {
      checkConnectionQuality();
    }

    // Event listeners for online/offline status
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Add listener for our custom test event
    window.addEventListener('app:connection-change', handleConnectionChange);

    // Clean up
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('app:connection-change', handleConnectionChange);
    };
  }, []);

  return { 
    isOnline, 
    connectionQuality 
  };
};
